/* global chartData, chartLeagueData, serverParams */
import * as d3 from 'd3'
import debounce from 'lodash/debounce'
/* eslint-disable-next-line */
import { Delaunay } from 'd3-delaunay'

const chartTypes = ['catcher', 'catching-team', 'league']
const {
    appSettings,
} = require('bam-node-baseball-savant/leaderboard/appSettings')

if (!chartTypes.includes(serverParams.type)) {
    return
}

const svg = d3.select('#catcher-stance-viz')

const narrowWidth = 800
const veryNarrowWidth = 500

let boundedWidth = svg.node().getBoundingClientRect().width
let isNarrow
let boundedHeight = Number(svg.node().getAttribute('height')) || 500

// Make variable
const marginWide = { top: 80, right: 50, bottom: 50, left: 70 }
const marginNarrow = { top: 60, right: 125, bottom: 50, left: 50 }
let margin = isNarrow ? marginNarrow : marginWide

let width = boundedWidth - margin.left - margin.right
let height = boundedHeight - margin.top - margin.bottom

const minYear = appSettings.SETTINGS_CATCHER_STANCE.firstYear
const maxYear = appSettings.SETTINGS_CATCHER_STANCE.year

const LEAGUE_CODE = 999999
const TRANSITION_DURATION = 100
const ZOOM_LEVEL = 1.6
const HEAD_RADIUS = 10
const BUBBLE_OFFSET = 10
const TEXT_BOX_MARGIN = 10

const LINE_DEFAULT_OPACITY = 0.8
const LABEL_OPACITY = 0.85
const LEAGUE_LINE_OPACITY = 0.9
const LINE_FADED_OPACITY = 0.1
const BUBBLE_FADED_OPACITY = 0.08

const NUM_INTERPOLATED_POINTS = 8
const INTERPOLATED_STEP = 1 / (NUM_INTERPOLATED_POINTS + 1)

const LINE_COLOR = '#3a9098'

let isClicked = false
let clickedID

let searchActive = false
let searchFilteredIDs = []

const sortedData = {}
const interpolatedSortedData = {}
let leagueData

const g = svg
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

let xAxis
let xAxisScale
let yAxis
let yAxisScale

let title
let xAxisText
let yAxisText

svg.append('defs')
    .append('clipPath')
    .attr('id', 'scatter-clip')
    .append('circle')
    .attr('cx', BUBBLE_OFFSET)
    .attr('cy', 0)
    .attr('r', HEAD_RADIUS)

setDims()
addStaticElements()

if (Object.keys(chartData).length > 0) {
    sortData()
    interpolateData()
    addLeagueData()
    addData()

    if (serverParams.type === 'league') {
        handleMouseOver(999999)
        isClicked = true
        clickedID = 999999
    }
    addVoronoi()
}

function sortData() {
    leagueData = chartLeagueData[LEAGUE_CODE].sort(
        (a, b) => Number(a.year) - Number(b.year)
    )

    const playerIDs = Object.keys(chartData)
    playerIDs.forEach((id) => {
        const unsorted = chartData[id]
        const sorted = unsorted
            .filter((d) => d.skeletal_pitches > 0)
            .sort((a, b) => Number(b.year) - Number(a.year))
        if (sorted.length > 0) {
            sortedData[id] = sorted
        }
    })
}

function interpolateData() {
    Object.keys(sortedData).forEach((dat) => {
        const thisData = sortedData[dat]
        const interpolatedData = [...thisData]

        for (let i = 0; i < thisData.length - 1; i++) {
            const yearIncrement = thisData[i + 1].year - thisData[i].year
            const pctIncrement =
                thisData[i + 1].pct_one_down - thisData[i].pct_one_down
            const segmentSlope = pctIncrement / yearIncrement

            for (
                let j = 1;
                j <= NUM_INTERPOLATED_POINTS * yearIncrement * -1;
                j++
            ) {
                const newDataPoint = {
                    year: thisData[i].year - INTERPOLATED_STEP * j,
                    pct_one_down:
                        thisData[i].pct_one_down -
                        INTERPOLATED_STEP * j * segmentSlope +
                        0.001 * Math.random(),
                    id: thisData[i].id,
                }
                interpolatedData.push(newDataPoint)
            }
        }

        interpolatedData.sort((a, b) => Number(b.year) - Number(a.year))

        interpolatedSortedData[dat] = interpolatedData
    })
}

function setDims() {
    boundedWidth = svg.node().getBoundingClientRect().width
    isNarrow = window.innerWidth <= narrowWidth
    const isVeryNarrow = window.innerWidth <= veryNarrowWidth

    margin = isNarrow ? marginNarrow : marginWide

    width = boundedWidth - margin.left - margin.right

    svg.attr('height', () => {
        if (isVeryNarrow) {
            return width * 1.5
        }
        if (isNarrow) {
            return width * 0.8
        }
        return 600
    })

    boundedHeight = Number(svg.node().getAttribute('height')) || 500
    height = boundedHeight - margin.top - margin.bottom

    xAxisScale = d3
        .scaleLinear()
        .domain([minYear - 1, maxYear + 1])
        .range([0, width])

    yAxisScale = d3.scaleLinear().domain([0, 1]).range([height, 0])
}

function addStaticElements() {
    // Title
    title = g
        .append('text')
        .attr('class', 'title')
        .attr('transform', `translate(${width / 2} ,-20)`)
        .attr('font-size', '1.4rem')
        .attr('text-anchor', 'middle')
        .attr('fill', LINE_COLOR)
        .text('Statcast Catcher Knee-Down Percent')

    // X Axis
    xAxis = g
        .append('g')
        .attr('transform', `translate(0, ${height})`)
        .call(
            d3
                .axisBottom(xAxisScale)
                .ticks(maxYear + 1 - minYear)
                .tickFormat((d) =>
                    d !== minYear - 1 && d !== maxYear + 1 ? d : null
                )
                .tickSize(-height)
                .tickPadding(10)
        )

    // X Axis label
    xAxisText = g
        .append('text')
        .attr('id', 'xLabel')
        .attr('transform', `translate(${width / 2} ,${height + 40})`)
        .style('text-anchor', 'middle')
        .style('font-size', '1rem')
        .text('Year')

    // Y Axis
    yAxis = g.append('g').call(
        d3
            .axisLeft(yAxisScale)
            .tickSize(-width)
            .tickFormat((d) => `${(d * 100).toFixed(0)}%`)
    )

    // Y Axis label
    yAxisText = g
        .append('text')
        .attr('id', 'yLabel')
        .attr('transform', 'rotate(-90)')
        .attr('y', 0 - margin.left / 2)
        .attr('x', 0 - height / 2)
        .attr('dy', '0em')
        .style('text-anchor', 'middle')
        .style('font-size', '1rem')
        .text('% Pitches')

    d3.selectAll('.tick line')
        .attr('stroke-opacity', 0.3)
        .attr('stroke-dasharray', '5,5')
}

function addLeagueData() {
    if (serverParams.type === 'league') {
        return
    }

    const line = d3
        .line()
        .x((d) => xAxisScale(d.year))
        .y((d) => yAxisScale(d.pct_one_down))

    const firstYearLeagueData = leagueData[0]

    g.append('path')
        .data(leagueData)
        .attr('class', 'league-line')
        .attr('id', 'league_ref_line')
        .attr('fill', 'none')
        .attr('stroke', 'red')
        .attr('stroke-width', 1.5)
        .attr('opacity', LEAGUE_LINE_OPACITY)
        .attr('stroke-dasharray', '5,5')
        .attr('d', line(leagueData))

    const leagueText = g
        .append('text')
        .attr('class', 'league-line')
        .attr('id', 'league_ref_text')
        .attr('fill', 'red')
        .attr('y', yAxisScale(firstYearLeagueData.pct_one_down))
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .style('font-size', 12)
        .text('League')

    const leagueWidth = leagueText.node().getComputedTextLength()
    leagueText.attr(
        'x',
        xAxisScale(firstYearLeagueData.year) - leagueWidth / 2 - 5
    )
}

function addData() {
    const line = d3
        .line()
        .x((d) => xAxisScale(d.year))
        .y((d) => yAxisScale(d.pct_one_down))

    const keys = Object.keys(sortedData)
    keys.forEach((key) => {
        const dataSeq = sortedData[key]
        g.append('path')
            .data(dataSeq)
            .attr('class', 'percent-line')
            .attr('id', `line_${key}`)
            .attr('fill', 'none')
            .attr('stroke', LINE_COLOR)
            .attr('stroke-width', 1.5)
            .attr('opacity', LINE_DEFAULT_OPACITY)
            .attr('d', line(dataSeq))
            .on('mouseover', (e, d) => {
                if (!isClicked) {
                    handleMouseOver(d.id)
                }
            })
            .on('mouseout', (e, d) => {
                if (!isClicked) {
                    handleMouseOut(d.id)
                }
            })
            .on('click', (e, d) => {
                if (isClicked) {
                    handleClearClick()
                } else {
                    handleMouseOver(d.id)
                    isClicked = true
                    clickedID = d.id
                }
            })

        const mostRecentYear = dataSeq[0]
        addPlayerBubble(mostRecentYear, key)
    })

    d3.selectAll('.player-bubble')
        .raise()
        .on('mouseover', (e) => {
            if (!isClicked) {
                handleMouseOver(getKeyFromEvent(e))
            }
        })
        .on('mouseout', (e) => {
            if (!isClicked) {
                handleMouseOut(getKeyFromEvent(e))
            }
        })
        .on('click', (e) => {
            if (!isClicked) {
                handleMouseOver(getKeyFromEvent(e))
                isClicked = true
                clickedID = getKeyFromEvent(e)
            } else {
                handleClearClick()
            }
        })

    svg.on('mousedown', (e) => {
        const targetID = e.target.id
        if (
            isClicked &&
            !targetID.startsWith('bubble_') &&
            !targetID.startsWith('line_') &&
            !targetID.startsWith('voronoi_path_')
        ) {
            handleClearClick()
        }
    })
}

function getKeyFromEvent(e) {
    return e.target.id.split('_')[1]
}

function handleMouseOver(id) {
    d3.selectAll('.percent-line').attr('opacity', LINE_FADED_OPACITY)
    d3.select(`#line_${id}`).attr('opacity', 1.0)
    d3.selectAll('.player-bubble').attr('opacity', BUBBLE_FADED_OPACITY)

    transitionBubble(id)
    addLabels(id)
    addPointDots(id)

    d3.select(`.player-bubble-${id}`).attr('opacity', 1.0).raise()
}

function handleMouseOut(id) {
    if (searchActive) {
        updateDataFromSearch(searchFilteredIDs, !searchActive, false)
    } else {
        d3.selectAll('.percent-line').attr('opacity', LINE_DEFAULT_OPACITY)
        d3.selectAll('.player-bubble').attr('opacity', 1.0)
    }
    transitionBubble(id, true)
    d3.selectAll('.player-hovered-label').remove()
    d3.selectAll(`.point-dot-${id}`).remove()
}

function handleClearClick() {
    if (isClicked && clickedID) {
        handleMouseOut(clickedID)
        isClicked = false
        clickedID = undefined
    }
}

function addPlayerBubble(mostRecentYear, key) {
    const imageBubble = g
        .append('g')
        .attr(
            'transform',
            `translate(${xAxisScale(mostRecentYear.year) + 3},${yAxisScale(
                mostRecentYear.pct_one_down
            )})`
        )
        .attr('class', `player-bubble player-bubble-${key}`)
        .attr('id', `bubble_${key}_wrapper`)
        .attr('clip-path', 'url(#scatter-clip)')

    imageBubble
        .append('circle')
        .attr('cx', BUBBLE_OFFSET)
        .attr('cy', 0)
        .attr('r', HEAD_RADIUS)
        .attr('fill', 'white')
        .attr('class', `player-bubble-${key}`)
        .attr('stroke', 'black')
        .attr('id', `bubble_${key}_1`)
        .style('pointer-events', 'none')

    imageBubble
        .append('image')
        .attr('href', getImageURL(key))
        .attr('x', 0)
        .attr('y', -BUBBLE_OFFSET)
        .attr('width', HEAD_RADIUS * 2)
        .attr('height', HEAD_RADIUS * 2)
        .attr('class', `player-bubble-${key}`)
        .style('pointer-events', 'none')
        .attr('id', `bubble_${key}_img`)

    imageBubble
        .append('circle')
        .attr('cx', BUBBLE_OFFSET)
        .attr('cy', 0)
        .attr('r', HEAD_RADIUS)
        .attr('fill', 'none')
        .attr('class', `player-bubble-${key}`)
        .attr('stroke', 'black')
        .attr('id', `bubble_${key}_2`)

    imageBubble
        .append('circle')
        .attr('id', `bubble_${key}_3`)
        .attr('class', `data-bubble player-bubble-${key}`)
        .attr('cx', BUBBLE_OFFSET)
        .attr('cy', 0)
        .attr('r', HEAD_RADIUS)
        .attr('fill', 'white')
        .attr('stroke', 'black')
        .attr('fill-opacity', 0)
        .data(mostRecentYear)
}

function getImageURL(key) {
    if (serverParams.type === 'league') {
        return '/site-core/images/savant-mlb.svg'
    }
    if (serverParams.type === 'catching-team') {
        return `https://midfield.mlbstatic.com/v1/team/${key}/spots/120`
    }
    return `https://img.mlbstatic.com/mlb-photos/image/upload/w_100,d_people:generic:headshot:silo:current.png,q_auto:best,f_auto/v1/people/${key}/headshot/silo/current`
}

function transitionBubble(id, pop = false) {
    const zoomScale = pop ? 1 : ZOOM_LEVEL
    const mostRecentYear = sortedData[id][0]
    const horizOffset = pop ? 3 : 3 - HEAD_RADIUS * (zoomScale - 1)

    d3.select(`.player-bubble-${id}`)
        .transition()
        .duration(TRANSITION_DURATION)
        .attr(
            'transform',
            `translate(${
                xAxisScale(mostRecentYear.year) + horizOffset
            },${yAxisScale(mostRecentYear.pct_one_down)}) scale(${zoomScale})`
        )
}

function addLabels(id) {
    const sortedPlayerData = sortedData[id]
    const mostRecentData = sortedPlayerData[0]

    addNameAndMostRecentYear(mostRecentData)
    if (sortedPlayerData.length > 1) {
        addOtherYears(sortedPlayerData.slice(1))
    }
}

function addPointDots(id) {
    const sortedPlayerData = sortedData[id]

    g.selectAll(`.point-dot-${id}`)
        .data(sortedPlayerData)
        .join((enter) => {
            enter
                .append('circle')
                .attr('r', 3)
                .attr('class', `point-dot-${id}`)
                .attr('cx', (d) => xAxisScale(d.year))
                .attr('cy', (d) => yAxisScale(d.pct_one_down))
                .attr('stroke', LINE_COLOR)
                .attr('fill', LINE_COLOR)
        })
}

function addNameAndMostRecentYear(mostRecentData) {
    const mostRecentX = xAxisScale(mostRecentData.year)
    const mostRecentY = yAxisScale(mostRecentData.pct_one_down)

    const labelWrapper = g
        .append('g')
        .attr('transform', `translate(${mostRecentX + 30},${mostRecentY - 15})`)
        .attr('class', 'player-hovered-label')
        .style('pointer-events', 'none')

    const box = labelWrapper
        .append('rect')
        .attr('fill', '#222')
        .attr('fill-opacity', LABEL_OPACITY)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .attr('x', 0)
        .attr('y', 0)
        .attr('rx', 5)
        .attr('height', 50)
        .attr('class', 'label-box')

    const nameText = labelWrapper
        .append('text')
        .attr('fill', '#399098')
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .text(mostRecentData.name)
        .attr('y', 15)
        .attr('class', 'label-box')
        .style('font-size', 18)

    const valText = labelWrapper
        .append('text')
        .attr('fill', '#399098')
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .text(
            `${mostRecentData.year}: ${(
                mostRecentData.pct_one_down * 100
            ).toFixed(0)}%`
        )
        .attr('y', 37)
        .attr('class', 'label-box')
        .style('font-size', 14)

    const labelWidth = nameText.node().getComputedTextLength()
    const valWidth = valText.node().getComputedTextLength()
    const maxWidth = Math.max(labelWidth, valWidth)

    box.attr('width', maxWidth + 2 * TEXT_BOX_MARGIN)
    nameText.attr('x', maxWidth / 2 + TEXT_BOX_MARGIN)
    valText.attr('x', maxWidth / 2 + TEXT_BOX_MARGIN)
}

function addOtherYears(slicedData) {
    slicedData.forEach((d) => {
        const refX = xAxisScale(d.year)
        const refY = yAxisScale(d.pct_one_down)

        const labelWrapper = g
            .append('g')
            .attr('transform', `translate(${refX},${refY})`)
            .style('pointer-events', 'none')
            .attr('class', 'player-hovered-label')

        const box = labelWrapper
            .append('rect')
            .attr('fill', '#222')
            .attr('fill-opacity', LABEL_OPACITY)
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .attr('y', 6)
            .attr('rx', 5)
            .attr('height', 20)
            .attr('class', 'label-box')
            .style('pointer-events', 'none')

        const valText = labelWrapper
            .append('text')
            .attr('fill', '#399098')
            .attr('dominant-baseline', 'middle')
            .attr('text-anchor', 'middle')
            .attr('fill', 'white')
            .text(`${d.year}: ${(d.pct_one_down * 100).toFixed(0)}%`)
            .attr('x', 0)
            .attr('y', 17)
            .attr('class', 'label-box')
            .style('font-size', 14)
            .style('pointer-events', 'none')

        const valWidth = valText.node().getComputedTextLength()
        box.attr('width', valWidth + 2 * TEXT_BOX_MARGIN).attr(
            'x',
            -valWidth / 2 - TEXT_BOX_MARGIN
        )
    })
}

function updateAxes() {
    const newXAxis = d3
        .axisBottom(xAxisScale)
        .ticks(maxYear + 1 - minYear)
        .tickFormat((d) => (d !== minYear - 1 && d !== maxYear + 1 ? d : null))
        .tickSize(-height)
        .tickPadding(10)

    xAxis
        .transition()
        .call(newXAxis)
        .attr('transform', `translate(0, ${height})`)

    xAxisText
        .transition()
        .attr('transform', `translate(${width / 2} ,${height + 40})`)

    const newYAxis = d3
        .axisLeft(yAxisScale)
        .tickSize(-width)
        .tickFormat((d) => `${(d * 100).toFixed(0)}%`)

    yAxis.transition().call(newYAxis)

    yAxisText
        .transition()
        .attr('y', 0 - margin.left / 2)
        .attr('x', 0 - height / 2)

    title.transition().attr('transform', `translate(${width / 2} ,-20)`)
}

function updateData() {
    const line = d3
        .line()
        .x((d) => xAxisScale(d.year))
        .y((d) => yAxisScale(d.pct_one_down))

    const leagueWidth = d3
        .select('#league_ref_text')
        .node()
        .getComputedTextLength()

    d3.select('.league-line').transition().attr('d', line(leagueData))
    d3.select('#league_ref_text')
        .transition()
        .attr('y', yAxisScale(leagueData[0].pct_one_down))
        .attr('x', xAxisScale(leagueData[0].year) - leagueWidth / 2 - 5)

    const idArray = Object.keys(sortedData)
    idArray.forEach((id) => {
        const mostRecentYear = sortedData[id][0]
        d3.select(`#line_${id}`).transition().attr('d', line(sortedData[id]))
        d3.select(`.player-bubble-${id}`)
            .transition()
            .attr(
                'transform',
                `translate(${xAxisScale(mostRecentYear.year) + 3},${yAxisScale(
                    mostRecentYear.pct_one_down
                )})`
            )
    })
}

function addVoronoi() {
    const dataValues = Object.values(interpolatedSortedData).flat()

    // Voronoi debugging code

    // g.selectAll('.circle-test')
    //     .data(dataValues)
    //     .join((enter) => {
    //         enter
    //             .append('circle')
    //             .attr('r', 2)
    //             .attr('cx', (d) => xAxisScale(d.year))
    //             .attr('cy', (d) => yAxisScale(d.pct_one_down))
    //             .attr('fill', 'black')
    //     })

    const voronoi = Delaunay.from(
        dataValues,
        (d) => xAxisScale(d.year),
        (d) => yAxisScale(d.pct_one_down)
    ).voronoi([
        xAxisScale(appSettings.SETTINGS_CATCHER_STANCE.firstYear - 0.5),
        0,
        width - xAxisScale(appSettings.SETTINGS_CATCHER_STANCE.firstYear - 0.5),
        height,
    ])

    g.append('g')
        .attr('fill', 'none')
        // .attr('stroke', 'black')
        .attr('pointer-events', 'all')
        .attr('class', 'stance-voronoi-grid')
        .selectAll('path')
        .data(dataValues)
        .join('path')
        .attr('d', (d, i) => voronoi.renderCell(i))
        .attr('id', (d) => `voronoi_path_${d.id}`)
        .on('mouseover', (e, d) => {
            if (
                !isClicked &&
                (!searchActive ||
                    (searchActive && searchFilteredIDs.includes(d.id)))
            ) {
                handleMouseOver(d.id)
            }
        })
        .on('mouseout', (e, d) => {
            if (
                !isClicked &&
                (!searchActive ||
                    (searchActive && searchFilteredIDs.includes(d.id)))
            ) {
                handleMouseOut(d.id)
            }
        })
        .on('click', (e, d) => {
            if (isClicked) {
                handleClearClick()
            } else {
                handleMouseOver(d.id)
                isClicked = true
                clickedID = d.id
            }
        })

    d3.selectAll('.player-bubble').attr('opacity', 1.0).raise()
}

// eslint-disable-next-line
export function updateDataFromSearch(filteredIDs, isSearchEmpty, firstLoop = true) {
    searchActive = !isSearchEmpty

    if (firstLoop) {
        handleClearClick()
    }

    if (searchActive) {
        searchFilteredIDs = [...filteredIDs]
        Object.keys(sortedData).forEach((idKey) => {
            const id = Number(idKey)
            if (!filteredIDs.includes(id)) {
                d3.select(`#line_${id}`)
                    .attr('opacity', LINE_FADED_OPACITY)
                    .style('pointer-events', 'none')
                d3.select(`.player-bubble-${id}`)
                    .attr('opacity', BUBBLE_FADED_OPACITY)
                    .style('pointer-events', 'none')
            } else {
                d3.select(`#line_${id}`)
                    .attr('opacity', LINE_DEFAULT_OPACITY)
                    .raise()
                d3.select(`.player-bubble-${id}`)
                    .attr('opacity', 1.0)
                    .style('pointer-events', 'all')
                    .raise()
            }
        })
        if (searchFilteredIDs.length === 1) {
            const loneID = searchFilteredIDs[0]
            handleMouseOver(loneID)
            isClicked = true
            clickedID = loneID
        }
    } else {
        d3.selectAll('.percent-line')
            .attr('opacity', LINE_DEFAULT_OPACITY)
            .style('pointer-events', 'all')
        d3.selectAll('.player-bubble')
            .attr('opacity', 1.0)
            .style('pointer-events', 'all')
    }
}

window.addEventListener(
    'resize',
    debounce(() => {
        handleClearClick()
        setDims()
        g.attr('transform', `translate(${margin.left},${margin.top})`)

        updateAxes()
        updateData()

        d3.select('.stance-voronoi-grid').remove()
        addVoronoi()
    }, 300)
)
